<template>
  <Grid class="h-full m-0" v-if="recommendations.length > 0">
    <Col7 class="lg:col-9 p-0 pr-3">
      <div class="flex flex-column h-full line-height-2">
        <h3 class="text-3xl font-semibold line-height-2 text-gray-700">
          {{ recHeader }}
        </h3>
        <p class="flex-1 text-lg line-height-2 text-gray-700">
          {{ recDescription }}
        </p>
        <div class="flex">
          <Button
            :label="expandedRecBtnLabel"
            class="p-button-secondary p-button-outlined font-semibold"
            @click="goToRecommendationDetails"
          />
        </div>
      </div>
    </Col7>
    <Col5 class="surface-border border-left-1 lg:col-3 pr-0">
      <div class="flex flex-column h-full">
        <div class="flex justify-content-between mb-3">
          <h4 class="m-0 pl-4 font-semibold">{{ chartHeader }}</h4>
          <i
            class="material-symbols-rounded text-primary pr-2"
            v-tooltip.left="{
              value: chartTooltip,
            }"
          >
            info
          </i>
        </div>
        <Chart
          type="bar"
          :data="chartData"
          :options="chartOptions"
          :canvasProps="{
            role: 'img',
            'aria-label': translate(`${LOCALE_PREFIX}.recommendations.chartAriaLabel`),
          }"
        />
      </div>
    </Col5>
  </Grid>

  <EmptyRecommendations v-if="recommendations.length === 0" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Chart from 'primevue/chart';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useRecommendationsStore } from '../../../../stores/use-recommendations-store';
import { storeToRefs } from 'pinia';
import { useTranslation } from '@centric-os/helpers';
import useRecommendations from '../../../../../composables/helpers/use-recommendations';
import type { ChartData, ChartDataset, ChartOptions } from '@centric-os/types/api/recommendations';
import { RecommendationType } from '@centric-os/types/api/recommendations';
import EmptyRecommendations from './empty/EmptyRecommendations.vue';
import { SitesRouteNames, SitesLocalePrefix } from '../../../../../enums';
import { useRoute, useRouter } from 'vue-router';

// props
defineProps<{
  hideDetailsButton?: boolean;
}>();

enum UnitTypes {
  PERCENT = 'percent',
  CURRENCY = 'currency',
}

enum ChartColours {
  FIRST_BAR = '#BF51AA',
  SECOND_BAR = '#463EAD',
}

// store and store refs
const recommendationsStore = useRecommendationsStore();
const { currentRecommendation, recommendations, currentRecommendationType } =
  storeToRefs(recommendationsStore);

// translation
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_OVERVIEW;

// composables
const { formatTickStrings, parsedVisual, recHeader, recDescription } =
  useRecommendations(translate);

// routing
const router = useRouter();
const route = useRoute();

const goToRecommendationDetails = (): void => {
  router.push({
    name: SitesRouteNames.SITE_RECOMMENDATION_DETAILS,
    params: {
      siteId: route.params.siteId,
      recommendationId:
        currentRecommendation.value?.recommendation_id ?? currentRecommendation.value?.rec_id,
    },
  });
};

const expandedRecBtnLabel = computed<string>(() => {
  switch (currentRecommendationType.value) {
    case RecommendationType.CPG:
      return translate(`${LOCALE_PREFIX}.recommendations.expandedRecBtnLabel.cpg`);
    case RecommendationType.EXPAND:
      return translate(`${LOCALE_PREFIX}.recommendations.expandedRecBtnLabel.expand`);
    case RecommendationType.REDUCE:
      return translate(`${LOCALE_PREFIX}.recommendations.expandedRecBtnLabel.reduce`);
    default:
      return '';
  }
});

const chartHeader = computed<string>(() => {
  if (currentRecommendationType.value === RecommendationType.CPG)
    return translate(`${LOCALE_PREFIX}.recommendations.chartHeader.cpg`);

  if (currentRecommendationType.value === RecommendationType.REDUCE)
    return translate(`${LOCALE_PREFIX}.recommendations.chartHeader.reduce`);

  return translate(`${LOCALE_PREFIX}.recommendations.chartHeader.expand`);
});

const chartTooltip = computed<string>(() => {
  if (currentRecommendationType.value === RecommendationType.CPG)
    return translate(`${LOCALE_PREFIX}.recommendations.chartTooltip.cpg`);

  if (currentRecommendationType.value === RecommendationType.REDUCE)
    return translate(`${LOCALE_PREFIX}.recommendations.chartTooltip.reduce`);

  return translate(`${LOCALE_PREFIX}.recommendations.chartTooltip.expand`);
});

ChartJS.register(ChartDataLabels);

const chartData = computed<ChartData | null>(() => {
  if (!parsedVisual.value) return null;

  const labels: string[] = parsedVisual.value.labels || [];
  const datasets: any[] = parsedVisual.value.datasets || [];

  const colors: string[] = [ChartColours.FIRST_BAR, ChartColours.SECOND_BAR];

  const chartDatasets: ChartDataset[] = datasets.map((dataset: any): ChartDataset => {
    const backgroundColor = dataset.data?.map(
      (_: any, dataIndex: number) => colors[dataIndex % colors.length],
    ) || [colors[0]];

    return {
      label: dataset.label,
      data: dataset.data,
      backgroundColor,
      borderRadius: 6,
      borderSkipped: 'bottom', // only round the top corners
      yAxisID: dataset.unit === UnitTypes.PERCENT ? 'y1' : 'y', // keeps bars with alike units scaling together (% scales with %, $ scales with $)
      unit: dataset.unit || '',
    };
  });

  return {
    labels,
    datasets: chartDatasets,
  };
});

const chartOptions = computed<ChartOptions | null>(() => {
  if (!parsedVisual.value) return null;

  return {
    maintainAspectRatio: false,
    aspectRatio: 0.7,
    maxBarThickness: 90,
    layout: {
      padding: {
        top: 25, // keeps space within canvas for data labels for charts with tall bars
      },
    },
    plugins: {
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          boxWidth: 6,
          boxHeight: 6,
          padding: 8,
          font: {
            size: 12,
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        font: {
          size: 12,
          weight: 'bold',
        },
        formatter: function (value: number, context: any): string | number {
          const unit: string = context.dataset.unit || '';
          const formattedValue = value >= 1000 ? value.toLocaleString() : value;

          if (unit === UnitTypes.CURRENCY) {
            return `$${formattedValue}`;
          } else if (unit === UnitTypes.PERCENT) {
            return `${formattedValue}%`;
          } else {
            return formattedValue;
          }
        },
      },
    },
    scales: {
      y: {
        display: false,
      },
      y1: {
        display: false,
      },
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: any, index: number): string | string[] {
            const chartLabels: string[] = chartData.value?.labels || [];
            const label: string = chartLabels[index] as string;
            return formatTickStrings(label);
          },
        },
      },
    },
  };
});
</script>

<style scoped lang="scss">
:deep(.p-chart) {
  min-height: 250px !important;
  height: 100% !important;
}
</style>
